import { CheckCircleIcon, CheckIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { ExclamationCircleIcon } from '@heroicons/react/24/solid';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useState } from 'react';
import { type FieldError, type FieldErrorsImpl, type FieldValues, type Merge, useForm } from 'react-hook-form';
import EasyVoteLogo from 'shared/src/components/EasyVoteLogo';
import Flexor from 'shared/src/components/Flexor';
import Spinner from 'shared/src/components/Spinner';
import { Alert, AlertDescription, AlertTitle, Button, Input, Label } from 'shared/src/components/ui/new';
import { SESSION_STORAGE_KEYS } from 'shared/src/constants';
import { cn } from 'shared/src/utils';
import { type RefinementCtx, z } from 'zod';

const FormSchema = z
  .object({
    resetCode: z.string().min(6, 'Password reset code is required'),
    password1: z
      .string()
      .min(8, { message: 'Password must be at least 8 characters long' })
      .regex(/[A-Z]/, { message: 'Password must contain at least 1 uppercase letter' })
      .regex(/[a-z]/, { message: 'Password must contain at least 1 lowercase letter' })
      .regex(/[0-9]/, { message: 'Password must contain at least 1 number' })
      .regex(/[\W_]/, { message: 'Password must contain at least 1 symbol' }),
    password2: z.string().min(1, 'Confirm new password is required'),
  })
  .superRefine((data: { password1: string; password2: string }, ctx: RefinementCtx) => {
    if (data.password1 !== data.password2) {
      ctx.addIssue({ message: 'Passwords do not match', path: ['password2'], code: z.ZodIssueCode.custom });
    }
  });

export interface ResetPasswordProps {
  startPasswordReset: (username: string) => Promise<boolean>;
  performPasswordReset: (resetCode: string, password: string, userId: string) => Promise<boolean>;
  onSuccessfulReset: (username: string) => void;
  errorMessage: string | undefined;
  isPasswordReset: boolean;
  loading: boolean;
  className?: string;
  framed?: boolean;
}

function ErrorMessage({ error }: { error: FieldError | Merge<FieldError, FieldErrorsImpl<FieldValues>> | undefined }) {
  if (!error || !error.message) return null;

  return <div className="absolute text-xs font-semibold text-destructive">{error.message.toString()}</div>;
}

export default function ResetPassword({
  startPasswordReset,
  performPasswordReset,
  onSuccessfulReset,
  errorMessage,
  isPasswordReset,
  loading,
  className,
  framed = false,
}: ResetPasswordProps) {
  const [resentCode, setResentCode] = useState<boolean>(false);
  const [resendingCode, setResendingCode] = useState<boolean>(false);

  const userId = window.sessionStorage.getItem(SESSION_STORAGE_KEYS.FORGOT_PASSWORD_USER_ID);

  const {
    register,
    handleSubmit,
    setValue,
    setFocus,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(FormSchema),
  });

  function submit(data: FieldValues) {
    if (!userId) return;

    performPasswordReset(data.resetCode, data.password1, userId).then((success) => {
      if (success) {
        onSuccessfulReset(userId);
        window.sessionStorage.removeItem(SESSION_STORAGE_KEYS.FORGOT_PASSWORD_USER_ID);
      }
    });
  }

  function resendCode() {
    if (!userId) return;

    setResendingCode(true);

    startPasswordReset(userId)
      .then(() => {
        setResentCode(true);
        setValue('resetCode', '');
        setFocus('resetCode');
        setTimeout(() => setResentCode(false), 5000);
      })
      .finally(() => {
        setResendingCode(false);
      });
  }

  return (
    <div className={cn('theme-ev flex min-h-full flex-col justify-center py-12 xs:px-6 lg:px-8', className)}>
      <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
        <EasyVoteLogo className="mx-auto h-14 w-auto" />
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className={cn(framed && 'bg-white p-6 shadow sm:rounded-lg sm:p-8')}>
          <h2 className="mb-4 text-lg font-semibold text-foreground">Reset password</h2>
          {errorMessage ? (
            <Alert className="mb-6" variant="destructive">
              <ExclamationCircleIcon className="size-5" />
              <AlertTitle>Reset failed</AlertTitle>
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          ) : null}
          {isPasswordReset ? (
            <Alert className="mb-6" variant="default">
              <CheckCircleIcon className="size-4" />
              <AlertTitle>Your password has been reset!</AlertTitle>
            </Alert>
          ) : null}
          <Alert className="mb-6" variant="default">
            <InformationCircleIcon className="size-4" />
            <AlertDescription>
              <p>An authorization code has been sent to your device or email address.</p>
              <p className="mt-2">Once you receive the code, enter it below. Then enter your new password and confirm it.</p>
              <p className="mt-2">If you did not receive the authorization code, click "Resend" below to request a new one.</p>
            </AlertDescription>
          </Alert>
          <form onSubmit={handleSubmit(submit)} className="space-y-6 divide-border">
            <div>
              <Label htmlFor="resetCode">Password reset code</Label>
              <div className="mt-2">
                <Input id="resetCode" type="number" autoFocus={true} placeholder="Reset code" required {...register('resetCode')} />
                <ErrorMessage error={errors.resetCode} />
              </div>
            </div>

            <div className="space-y-4 pt-4">
              <div>
                <Label htmlFor="password1">New password</Label>
                <div className="mt-2">
                  <Input id="password1" type="password" autoComplete="new-password" placeholder="New password" required {...register('password1')} />
                  <ErrorMessage error={errors.password1} />
                </div>
              </div>

              <div>
                <Label htmlFor="password2">Confirm new password</Label>
                <div className="mt-2">
                  <Input id="password2" type="password" autoComplete="new-password" placeholder="Confirm new password" required {...register('password2')} />
                  <ErrorMessage error={errors.password2} />
                </div>
              </div>
            </div>

            <Flexor>
              <Flexor className="text-sm font-semibold space-x-1">
                <Button type="button" variant="link" tabIndex={-1} onClick={resendCode} className="h-auto p-0">
                  Resend
                </Button>
                <div className="scale-75">
                  <Spinner show={resendingCode} />
                </div>
                {resentCode ? <CheckIcon className="size-4 text-green-500" /> : null}
              </Flexor>
              <Button type="submit" disabled={loading} className="flex items-center space-x-2">
                <Spinner show={loading} light />
                <span>Reset password</span>
              </Button>
            </Flexor>
          </form>
        </div>
      </div>
    </div>
  );
}
